import { type TypedDay, parseTypedDay } from "$root/components/date-picker-popover";
import { serverTodayAsLocalDate } from "$root/utils";
import { DateTime } from "luxon";

/**
 * Date rules for the portfolio "as of" (history) view mode (MDOT-24733).
 *
 * All dates are `YYYY-MM-DD` strings in the user's local calendar; validity is
 * anchored to the server's calendar (Europe/Rome) per the conventions in
 * `$root/utils/dates`.
 */

/** The Portfolio Details search param that pins the page to an as-of day (`YYYY-MM-DD`). */
export const AS_OF_SEARCH_PARAM = "asOf";

/** The Portfolio Details search param that shows the proposal, when it is `"true"` and no as-of day is set. */
export const PROPOSAL_SEARCH_PARAM = "proposal";

/**
 * The earliest day the page can be pinned to: the day after the portfolio's inception.
 *
 * The backend builds a snapshot only for a date strictly after the portfolio was created and refuses
 * the inception day itself, so offering that day pins the page to a date whose generation can never
 * succeed. `undefined` when the inception date is unknown, which leaves the bound open.
 *
 * @param inceptionDate `YYYY-MM-DD` or a full ISO timestamp.
 */
export function earliestAsOfDay(inceptionDate?: string | null): string | undefined {
	const inceptionDay = inceptionDate?.slice(0, 10);
	if (!inceptionDay) {
		return undefined;
	}
	return DateTime.fromISO(inceptionDay).plus({ days: 1 }).toISODate() ?? undefined;
}

/**
 * Validates a raw `?asOf` URL param into a usable as-of date (R8).
 *
 * Returns `null` (meaning: live view, drop the param) when the value is
 * unparsable, does not follow the portfolio's inception date, or is not
 * strictly before server-today. Every day in that range is selectable,
 * weekends included.
 *
 * @param raw the raw query-param value
 * @param opts.inceptionDate the portfolio's inception date (`YYYY-MM-DD` or a
 *        full ISO timestamp); when absent the inception check is skipped.
 */
export function parseAsOfParam(
	raw: string | null | undefined,
	opts: { inceptionDate?: string | null } = {},
): string | null {
	if (!raw) {
		return null;
	}
	const parsed = DateTime.fromISO(raw);
	if (!parsed.isValid || parsed.toISODate() !== raw) {
		return null;
	}
	const earliestDay = earliestAsOfDay(opts.inceptionDate);
	if (earliestDay && raw < earliestDay) {
		return null;
	}
	if (raw >= serverTodayAsLocalDate()) {
		return null;
	}
	return raw;
}

/**
 * Reads what the user typed into the as-of field, in their locale's short date format.
 *
 * Deliberately settled by `parseAsOfParam`, the same function that vets `?asOf`: what can be typed and
 * what can be linked to are then one set by construction, rather than two that agree until someone
 * changes one of them.
 */
export function parseTypedAsOfDate(
	typed: string,
	opts: { locale?: string; inceptionDate?: string | null } = {},
): TypedDay {
	return parseTypedDay(typed, {
		locale: opts.locale,
		accept: (day) => parseAsOfParam(day, { inceptionDate: opts.inceptionDate }),
	});
}
